/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule, NgOptimizedImage } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { MenuModule, Menu } from "primeng/menu";
import { MenuItem } from "primeng/api";
import { RippleModule } from "primeng/ripple";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";

// Models
import { IProductSummary, IProductListResponse } from "../../models";

// Components
import {
  ProductCardComponent,
  IProductCardAction,
} from "../product-card/product-card.component";
import {
  ProductExpandedRowComponent,
  IProductExpandedRowAction,
} from "../product-expanded-row/product-expanded-row.component";
import {
  TableSkeletonRowsComponent,
  SkeletonColumnConfig,
  SKELETON_PRESETS,
} from "../../../../shared/components/table-skeleton";

/**
 * Product row action event
 */
export interface ProductRowAction {
  action:
    | "view"
    | "edit"
    | "sync"
    | "syncPrice"
    | "syncSalePrice"
    | "syncPrices"
    | "syncSalePrices"
    | "delete";
  product: IProductSummary;
}

/**
 * Product availability change event
 */
export interface ProductAvailabilityChange {
  product: IProductSummary;
  field: "AddToScheduledOrder" | "ProcessScheduledOrder";
  value: boolean;
}

/**
 * Sort event for products table
 */
export interface ProductSortEvent {
  field: string;
  order: number; // 1 for ascending, -1 for descending
}

/**
 * Page event for products table
 */
export interface ProductPageEvent {
  page: number;
  pageSize: number;
  first: number;
}

/**
 * Table column configuration
 */
export interface ProductTableColumn {
  field: string;
  header: string;
  width?: string;
  minWidth?: string;
  sortable?: boolean;
  visible?: boolean;
}

/**
 * Default table columns configuration
 * Note: Image, SKU, and Price columns removed as they are not in the API response
 */
export const DEFAULT_PRODUCT_TABLE_COLUMNS: ProductTableColumn[] = [
  {
    field: "ProductName",
    header: "Product",
    sortable: true,
    minWidth: "200px",
  },
  { field: "ProductType", header: "Type", sortable: true, width: "100px" },
  { field: "Stock", header: "Stock", sortable: true, width: "80px" },
  { field: "Active", header: "Status", sortable: true, width: "100px" },
];

/**
 * Page size options for pagination
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 25, 50];

/**
 * Products Table Component
 * PrimeNG p-table implementation with selection and row actions
 * Following scheduled-orders-table pattern for consistency
 */
@Component({
  selector: "app-products-table",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgOptimizedImage,
    TableModule,
    ButtonModule,
    CheckboxModule,
    MenuModule,
    RippleModule,
    TagModule,
    TooltipModule,
    SkeletonModule,
    ProductCardComponent,
    ProductExpandedRowComponent,
    TableSkeletonRowsComponent,
  ],
  templateUrl: "./products-table.component.html",
  styleUrl: "./products-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsTableComponent implements OnInit, OnDestroy, OnChanges {
  // Input properties
  @Input() data: IProductListResponse | null = null;
  @Input() loading = false;
  @Input() columns: ProductTableColumn[] = DEFAULT_PRODUCT_TABLE_COLUMNS;
  @Input() showCheckboxes = true;
  @Input() showActions = true;
  @Input() scrollHeight = "flex";
  @Input() responsiveLayout: "stack" | "scroll" = "stack";
  @Input() breakpoint = "768px";

  @Input() set selection(value: IProductSummary[]) {
    if (value) {
      this.selectedItems = [...value];
    }
  }

  // Track which products are currently updating availability (shows spinner)
  @Input() updatingAvailabilityIds: Set<number> = new Set();

  // Output events
  @Output() sortChanged = new EventEmitter<ProductSortEvent>();
  @Output() pageChanged = new EventEmitter<ProductPageEvent>();
  @Output() selectionChanged = new EventEmitter<IProductSummary[]>();
  @Output() rowAction = new EventEmitter<ProductRowAction>();
  @Output() availabilityChanged = new EventEmitter<ProductAvailabilityChange>();

  // Component state
  private readonly destroy$ = new Subject<void>();
  selectedItems: IProductSummary[] = [];
  expandedRows: { [key: string]: boolean } = {};
  contextMenuItems: MenuItem[] = [];
  currentContextProduct: IProductSummary | null = null;

  // Page size options for template
  readonly pageSizeOptions = [...PAGE_SIZE_OPTIONS];

  // Skeleton columns configuration using preset
  readonly skeletonColumns: SkeletonColumnConfig[] = SKELETON_PRESETS.products;

  ngOnInit(): void {
    this.initializeContextMenu();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] && this.data) {
      // Clear selections when data changes
      this.selectedItems = [];
      this.selectionChanged.emit(this.selectedItems);
    }
  }

  /**
   * Initialize context menu items for row actions
   * Matching old QMC menu options
   */
  private initializeContextMenu(): void {
    this.contextMenuItems = [
      {
        label: "View Details",
        icon: "pi pi-eye",
        command: () => this.emitRowAction("view"),
      },
      { separator: true },
      {
        label: "Sync Price",
        icon: "pi pi-dollar",
        command: () => this.emitRowAction("syncPrice"),
      },
      {
        label: "Sync Sale Price",
        icon: "pi pi-tag",
        command: () => this.emitRowAction("syncSalePrice"),
      },
      { separator: true },
      {
        label: "Delete",
        icon: "pi pi-trash",
        command: () => this.emitRowAction("delete"),
      },
    ];
  }

  /**
   * Emit row action with current context product
   */
  private emitRowAction(action: ProductRowAction["action"]): void {
    if (this.currentContextProduct) {
      this.rowAction.emit({
        action,
        product: this.currentContextProduct,
      });
    }
  }

  // ==================== Selection Methods ====================

  /**
   * Check if all items on current page are selected
   */
  isAllSelected(): boolean {
    if (!this.data?.Items?.length) return false;
    return this.data.Items.every((item) =>
      this.selectedItems.some((s) => s.Id === item.Id),
    );
  }

  /**
   * Check if some (but not all) items are selected
   */
  isSomeSelected(): boolean {
    if (!this.data?.Items?.length || !this.selectedItems.length) return false;
    return !this.isAllSelected() && this.selectedItems.length > 0;
  }

  /**
   * Toggle select all items on current page
   */
  toggleSelectAll(): void {
    if (!this.data?.Items?.length) return;

    if (this.isAllSelected()) {
      // Deselect all on current page
      const currentIds = new Set(this.data.Items.map((i) => i.Id));
      this.selectedItems = this.selectedItems.filter(
        (i) => !currentIds.has(i.Id),
      );
    } else {
      // Select all on current page
      const currentSelected = new Set(this.selectedItems.map((i) => i.Id));
      const newItems = this.data.Items.filter(
        (i) => !currentSelected.has(i.Id),
      );
      this.selectedItems = [...this.selectedItems, ...newItems];
    }

    this.selectionChanged.emit(this.selectedItems);
  }

  /**
   * Handle selection change from table
   */
  onSelectionChange(): void {
    this.selectionChanged.emit(this.selectedItems);
  }

  // ==================== Table Event Handlers ====================

  /**
   * Handle sort event from table
   */
  onSort(event: { field: string; order: number }): void {
    this.sortChanged.emit({
      field: event.field,
      order: event.order,
    });
  }

  /**
   * Handle page change event from table
   */
  onPageChange(event: { first: number; rows: number }): void {
    const page = Math.floor(event.first / event.rows) + 1;
    this.pageChanged.emit({
      page,
      pageSize: event.rows,
      first: event.first,
    });
  }

  /**
   * Handle row double click
   */
  onRowDoubleClick(event: unknown): void {
    const rowEvent = event as { data: IProductSummary };
    if (rowEvent?.data) {
      this.rowAction.emit({
        action: "view",
        product: rowEvent.data,
      });
    }
  }

  /**
   * Toggle row expansion
   * Uses string keys and triggers change detection like scheduled-orders-table
   */
  toggleRowExpansion(product: IProductSummary): void {
    const key = product.Id.toString();
    if (this.expandedRows[key]) {
      delete this.expandedRows[key];
      this.expandedRows = { ...this.expandedRows }; // Trigger change detection
    } else {
      // Single row expansion mode - clear others first
      this.expandedRows = { [key]: true };
    }
  }

  /**
   * Handle row expand event
   */
  onRowExpand(event: { data: IProductSummary }): void {
    // Can be used for analytics or lazy loading expanded content
  }

  /**
   * Handle row collapse event
   */
  onRowCollapse(event: { data: IProductSummary }): void {
    // Can be used for cleanup
  }

  /**
   * Open context menu for a product
   */
  openContextMenu(
    event: MouseEvent,
    product: IProductSummary,
    menu: Menu,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.currentContextProduct = product;
    menu.toggle(event);
  }

  // ==================== Row Action Handlers ====================

  /**
   * Handle view action
   */
  onViewProduct(product: IProductSummary): void {
    this.rowAction.emit({ action: "view", product });
  }

  /**
   * Handle edit action
   */
  onEditProduct(product: IProductSummary): void {
    this.rowAction.emit({ action: "edit", product });
  }

  /**
   * Handle sync action
   */
  onSyncProduct(product: IProductSummary): void {
    this.rowAction.emit({ action: "sync", product });
  }

  // ==================== Helper Methods ====================

  /**
   * Get status severity for tag display
   */
  getStatusSeverity(
    product: IProductSummary,
  ): "success" | "secondary" | "danger" {
    if (product.Valid === false) return "danger";
    return product.Active ? "success" : "secondary";
  }

  /**
   * Get status text for display
   */
  getStatusText(product: IProductSummary): string {
    if (product.Valid === false) return "Invalid";
    return product.Active ? "Active" : "Inactive";
  }

  // ==================== Sync Status Methods ====================

  /**
   * Check if product is syncing (Active and Valid)
   */
  isSyncing(product: IProductSummary): boolean {
    return product.Active && product.Valid !== false;
  }

  /**
   * Get sync status message for tooltip
   * Following old QMC pattern
   */
  getSyncStatusMessage(product: IProductSummary): string {
    if (!product.Active) {
      return "Not Syncing: Product inactive";
    }
    if (product.Valid === false) {
      return (
        "Not Syncing: " +
        this.getTextFromProductValidationErrorCode(product.ValidationErrorCode)
      );
    }
    return "Syncing is active";
  }

  /**
   * Get text from product validation error code
   * Following old QMC pattern
   */
  getTextFromProductValidationErrorCode(
    validationErrorCode: number | undefined,
  ): string {
    switch (validationErrorCode) {
      case 1:
        return "Unsupported Type";
      case 2:
        return "Product Not Found";
      case 3:
        return "Invalid Parent Type";
      case 4:
        return "Missing External Id";
      default:
        return "Unknown Error";
    }
  }

  /**
   * Get sync status severity for tag display
   */
  getSyncStatusSeverity(
    product: IProductSummary,
  ): "success" | "secondary" | "danger" {
    if (product.Active && product.Valid !== false) {
      return "success";
    }
    return "secondary";
  }

  /**
   * Get stock level severity for tag display
   */
  getStockSeverity(
    product: IProductSummary,
  ): "success" | "warn" | "danger" | "secondary" | "info" | "contrast" {
    const stockLevel = product.StockLevel?.toLowerCase() ?? "";
    if (stockLevel.includes("outofstock") || stockLevel.includes("out")) {
      return "danger";
    }
    if (stockLevel.includes("low") || stockLevel.includes("backorder")) {
      return "warn";
    }
    if (stockLevel.includes("instock") || stockLevel.includes("in")) {
      return "success";
    }
    return "secondary";
  }

  /**
   * Get stock level text for display
   */
  getStockText(product: IProductSummary): string {
    const stock = product.Stock;
    if (stock === undefined || stock === null) return "N/A";
    if (stock === 0) return "Out";
    return stock.toString();
  }

  /**
   * Format stock level as human readable text
   * e.g., "InStock" → "In Stock", "OutOfStock" → "Out of Stock"
   */
  formatStockLevel(stockLevel: string | undefined): string {
    if (!stockLevel) return "N/A";
    // Insert space before capital letters and trim
    return stockLevel.replace(/([A-Z])/g, " $1").trim();
  }

  /**
   * Format price/lifetime value for display
   */
  formatPrice(price: number | undefined): string {
    if (price === undefined || price === null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }

  /**
   * Track by function for ngFor
   */
  trackByProductId(index: number, product: IProductSummary): number {
    return product.Id;
  }

  /**
   * Track by function for columns
   */
  trackByColumn(index: number, column: ProductTableColumn): string {
    return column.field;
  }

  /**
   * Get total column count for colspan calculations
   * Columns: Expand(1) + Checkbox(1?) + ID(1) + Product(1) + Type(1) + Stock(1) + ShippingClass(1) +
   *          LifetimeValue(1) + QtyScheduled(1) + Availability(1) + Active(1) + Paused(1) + Failed(1) + SyncStatus(1) + Actions(1?)
   */
  getColumnCount(): number {
    let count = 13; // Base columns: Expand, ID, Product, Type, Stock, ShippingClass, LifetimeValue, QtyScheduled, Availability, Active, Paused, Failed, SyncStatus
    if (this.showCheckboxes) count++;
    if (this.showActions) count++;
    return count;
  }

  // ==================== Availability Methods ====================

  /**
   * Check if product availability is currently being updated
   */
  isUpdatingAvailability(product: IProductSummary): boolean {
    return this.updatingAvailabilityIds.has(product.Id);
  }

  /**
   * Check if product has AddToScheduledOrder availability
   */
  hasAddToScheduledOrder(product: IProductSummary): boolean {
    return product.Availability?.includes("AddToScheduledOrder") ?? false;
  }

  /**
   * Check if product has ProcessScheduledOrder availability
   */
  hasProcessScheduledOrder(product: IProductSummary): boolean {
    return product.Availability?.includes("ProcessScheduledOrder") ?? false;
  }

  /**
   * Handle AddToScheduledOrder checkbox change
   */
  onAddToScheduledOrderChange(
    product: IProductSummary,
    checked: boolean,
  ): void {
    this.availabilityChanged.emit({
      product,
      field: "AddToScheduledOrder",
      value: checked,
    });
  }

  /**
   * Handle ProcessScheduledOrder checkbox change
   */
  onProcessScheduledOrderChange(
    product: IProductSummary,
    checked: boolean,
  ): void {
    this.availabilityChanged.emit({
      product,
      field: "ProcessScheduledOrder",
      value: checked,
    });
  }

  // ==================== Mobile Card Methods ====================

  /**
   * Check if a product is selected
   */
  isProductSelected(product: IProductSummary): boolean {
    return this.selectedItems.some((s) => s.Id === product.Id);
  }

  /**
   * Handle selection change from mobile card
   */
  onMobileCardSelectionChange(
    product: IProductSummary,
    selected: boolean,
  ): void {
    if (selected) {
      // Add to selection
      if (!this.isProductSelected(product)) {
        this.selectedItems = [...this.selectedItems, product];
      }
    } else {
      // Remove from selection
      this.selectedItems = this.selectedItems.filter(
        (s) => s.Id !== product.Id,
      );
    }
    this.selectionChanged.emit(this.selectedItems);
  }

  /**
   * Handle action from mobile card
   */
  onMobileCardAction(event: IProductCardAction): void {
    this.rowAction.emit({
      action: event.action,
      product: event.product,
    });
  }

  /**
   * Handle action from expanded row
   */
  onExpandedRowAction(event: IProductExpandedRowAction): void {
    this.rowAction.emit({
      action: event.action,
      product: event.product,
    });
  }

  /**
   * Handle mobile card click (for expansion)
   */
  onMobileCardClick(product: IProductSummary): void {
    this.toggleRowExpansion(product);
  }

  /**
   * Get mobile card status class
   */
  getMobileCardStatusClass(product: IProductSummary): string {
    if (!product.Active) {
      return "status-inactive";
    }
    if (product.StockLevel === "OutOfStock" || product.Stock === 0) {
      return "status-out-of-stock";
    }
    if (
      product.StockLevel === "LowStock" ||
      (product.Stock && product.Stock < 10)
    ) {
      return "status-low-stock";
    }
    return "status-active";
  }
}
